/**
 * Customers Zustand store.
 *
 * Built on top of the resource store factory so it inherits standard
 * list/get/create/update/delete + loading/error/pagination handling.
 * Adds module-specific state for the dashboard summary cards.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createResourceStore } from './createResourceStore';
import {
  Customer,
  UpdateCustomerInput,
  customersApi,
  CustomerSummary,
  getCustomerSummary,
} from '../services/customersApi';

export const useCustomersStore = createResourceStore<Customer, never, UpdateCustomerInput>(
  customersApi,
  { devtoolsName: 'customers-store', defaultQuery: { page: 1, limit: 20 } }
);

interface CustomerSummaryState {
  summary: CustomerSummary | null;
  loading: boolean;
  error: string | null;
  fetchSummary: () => Promise<void>;
  reset: () => void;
}

export const useCustomerSummaryStore = create<CustomerSummaryState>()(
  devtools(
    (set) => ({
      summary: null,
      loading: false,
      error: null,
      fetchSummary: async () => {
        set({ loading: true, error: null });
        try {
          const summary = await getCustomerSummary();
          set({ summary, loading: false });
        } catch (err) {
          set({
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load summary',
          });
        }
      },
      reset: () => set({ summary: null, loading: false, error: null }),
    }),
    { name: 'customer-summary-store' }
  )
);
